import { SUPPORTED_LOCALES, SeoPageType } from '@/constants';
import type { ICarrierInformation } from '@/interfaces';
import { minifyQuery, sanitizeGraphQLString } from '@/utils';
import callContentful from './base.fetchers';

export const DIRECTORY_PAGE_SIZE = 48;

export type DirectoryType = 'carriers' | 'regions';

// The existing seoPageType field already classifies every tracking page —
// carriers on one side, countries/regions/global market pages on the other.
export const SEO_PAGE_TYPES_BY_DIRECTORY: Record<DirectoryType, SeoPageType[]> = {
  carriers: [SeoPageType.carrier],
  regions: [SeoPageType.country, SeoPageType.region, SeoPageType.global],
};

export interface IDirectoryItem {
  slug: string;
  title: string;
  carrierLogo?: string | null;
}

interface IDirectoryItemResponse extends IDirectoryItem {
  carrierInformation?: Pick<ICarrierInformation, 'logoUrl'> | null;
}

export interface IDirectoryPageResult {
  items: IDirectoryItem[];
  total: number;
}

export const isSupportedDirectoryLocale = (locale?: string): locale is string =>
  !!locale && SUPPORTED_LOCALES.includes(locale);

type GetDirectoryPage = (args: {
  type: DirectoryType;
  page?: number;
  locale?: string;
  searchTerm?: string;
  limit?: number;
  isPreviewMode?: boolean;
}) => Promise<IDirectoryPageResult | null>;

export const getDirectoryPage: GetDirectoryPage = async ({
  type,
  page = 1,
  locale,
  searchTerm,
  limit = DIRECTORY_PAGE_SIZE,
  isPreviewMode = false,
}) => {
  const seoPageTypes = SEO_PAGE_TYPES_BY_DIRECTORY[type];
  const skip = (page - 1) * limit;

  // The locale lives inside the slug ("en/track-dhl"), so the locale filter is a
  // slug substring match — slugs hold a single "/" so "<locale>/" only ever
  // matches the prefix.
  const localeFilter = isSupportedDirectoryLocale(locale) ? `slug_contains: "${locale}/"` : '';
  const sanitizedSearchTerm = searchTerm ? sanitizeGraphQLString(searchTerm) : '';
  const searchFilter = sanitizedSearchTerm ? `title_contains: "${sanitizedSearchTerm}"` : '';

  const query = /* GraphQL */ `
    {
      landingPageCollection(
        preview: ${isPreviewMode}
        where: {
          seoPageType_in: [${seoPageTypes.map((seoPageType) => `"${seoPageType}"`).join(', ')}]
          slug_exists: true
          title_exists: true
          ${localeFilter}
          ${searchFilter}
        }
        order: [title_ASC]
        limit: ${limit}
        skip: ${skip}
      ) {
        total
        items {
          slug
          title
          carrierLogo
          carrierInformation
        }
      }
    }
  `;

  const response = await callContentful(
    minifyQuery(query),
    isPreviewMode,
    `/${type}`,
    'directory.fetchers.getDirectoryPage',
  );

  const collection = response?.data?.landingPageCollection;
  if (!collection) return null;

  return {
    items: (collection.items ?? [])
      .filter((item: IDirectoryItemResponse | null): item is IDirectoryItemResponse => !!item?.slug)
      .map(({ slug, title, carrierLogo, carrierInformation }: IDirectoryItemResponse) => ({
        slug,
        title,
        carrierLogo: carrierInformation?.logoUrl || carrierLogo || null,
      })),
    total: collection.total ?? 0,
  };
};
